import { MCPIntegrationType } from "@/pages/mcp/constants";
import { Teammate, type DatamateIntegration } from "@/pages/mcp/mcpSlice";

type Integration = DatamateIntegration;

interface Connection {
  integration_id: string;
  config: Record<string, string>;
}

/**
 * Validates datamate integrations and returns those with invalid configurations
 * @param datamate - The datamate to validate
 * @param allIntegrations - All available integrations
 * @param connections - All connections for the datamate
 * @returns Array of integrations with invalid configurations
 */
export const getIntegrationsWithoutValidConfig = (
  datamate: Teammate,
  allIntegrations: Integration[],
  connections: Connection[]
): Integration[] => {
  // Get integration data for datamate's integrations
  const datamateIntegrationsWithData =
    datamate.integrations
      ?.map(datamateIntegration => {
        const integrationData = allIntegrations.find(
          integration => integration.id === datamateIntegration.id
        );
        return integrationData;
      })
      .filter((i): i is Integration => !!i) || [];

  return datamateIntegrationsWithData.filter(integration => {
    if (!integration?.id) return false;
    if (integration.type === MCPIntegrationType.EXTENSION) return false;
    if (!integration.config?.length) return false;

    // Find connection for this integration and datamate
    const connection = connections.find(c => c.integration_id === integration.id);
    if (!connection) {
      return true; // No connection found, invalid config
    }

    // Check if connection has all required fields
    const requiredFields = integration.config
      .filter(c => c.required)
      .map(c => c.key)
      .filter((key): key is string => key !== undefined);
    if (!requiredFields.length) return false;

    return requiredFields.some(field => !connection.config[field]);
  });
};

/**
 * Checks if a datamate has any invalid integration configurations
 * @param datamate - The datamate to check
 * @param allIntegrations - All available integrations
 * @param connections - All connections for the datamate
 * @returns Boolean indicating if datamate has invalid configurations
 */
export const hasInvalidIntegrationConfig = (
  datamate: Teammate,
  allIntegrations: Integration[],
  connections: Connection[]
): boolean => {
  return getIntegrationsWithoutValidConfig(datamate, allIntegrations, connections).length > 0;
};
